import {
  OrganizationDTOOrganizationTypeIdEnum,
  OrganizationInternalOwnerDTOResponsibleForEnum,
  UserDTOInternalRolesEnum,
} from '@reposit/api-client';
import { get } from 'lodash';
import React, { useState } from 'react';
import { Col, Container, Row } from 'react-grid-system';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { getBreakpoint } from '../../base/style';
import { AssignAccountManagerModal } from '../../components/AssignAccountManagerModal';
import Button from '../../components/Button';
import IntegrationInfo from '../../components/IntegrationInfo';
import RepositCard from '../../components/Reposit/RepositCard';
import { Table, Tbody, Td, Th, Tr } from '../../components/Table';
import StatusItem from '../../components/TenantProgress/StatusItem';
import { UnassignAccountManagerConfirmModal } from '../../components/UnassignAccountManagerConfirmModal';
import { hasInternalRole } from '../../redux/account/account.selectors';
import { getOrganizationInternalOwners } from '../../redux/organization-internal-owner/organization-internal-owner.selectors';
import { AppState } from '../../redux/root.reducer';
import { getOrganization } from '../../redux/selectors/organization.selectors';

const StatusItems = styled.div`
  border-radius: 6px;
  overflow: hidden;
  display: block;
  justify-content: space-between;
  width: 100%;
  margin: 0 0 16px;

  @media all and (min-width: ${getBreakpoint('lg')}) {
    border-radius: 12px;
    align-items: center;
    display: flex;
    margin: 0;
  }
`;

const RowActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;

  button {
    margin-left: 8px;
  }
`;

interface OrganizationOnboardingProps {
  organizationId: string;
}

export const OrganizationAccount: React.FC<OrganizationOnboardingProps> = ({ organizationId }) => {
  const org = useSelector(getOrganization(organizationId));
  const internalOwners = useSelector((state: AppState) => getOrganizationInternalOwners(state, organizationId));
  const canManageAccountManagers = useSelector((state: AppState) =>
    hasInternalRole(state, UserDTOInternalRolesEnum.MANAGEORGANIZATIONACCOUNTMANAGER),
  );
  const salesOwner = internalOwners.find(
    (owner) => owner.responsibleFor === OrganizationInternalOwnerDTOResponsibleForEnum.SALES,
  );

  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [isUnassignOpen, setIsUnassignOpen] = useState(false);

  const attributes = org && org.attributes;

  const astStep = { title: 'AST', completed: !!(attributes && attributes.updatedAST) };
  const bankAccountStep = { title: 'Bank Accounts', completed: !!(attributes && attributes.bankAccountsConfirmed) };
  const usefulDocsStep = { title: 'Useful Documents', completed: !!(attributes && attributes.informedLandlords) };
  const welcomeStep = { title: 'Welcome', completed: !!(attributes && attributes.welcomeLandlordCompleted) };
  const tenancyGuideStep = { title: 'Tenancy Guide', completed: !!(attributes && attributes.landlordInformationCompleted) };
  const termsStep = { title: 'T&Cs', completed: !!(attributes && attributes.acceptedTerms) };
  const yourCompanyStep = { title: 'Your Company', completed: !!(attributes && attributes.yourCompanyComplete) };
  const integrationToken = get(attributes, 'legacyIntegrationToken', '');

  const steps =
    org && org.organizationTypeId === OrganizationDTOOrganizationTypeIdEnum.PRIVATE
      ? [welcomeStep, astStep, bankAccountStep, usefulDocsStep, tenancyGuideStep, termsStep]
      : [yourCompanyStep, astStep, bankAccountStep, usefulDocsStep, termsStep];

  const salesDisplayName = salesOwner ? salesOwner.internalTeamMember.displayName : 'Unassigned';

  return (
    <Container>
      <RepositCard title="Onboarding Status">
        <Row>
          <Col lg={12} style={{ padding: 0 }}>
            <StatusItems>
              {steps.map((item) => (
                <StatusItem key={item.title} label={item.title} status={item.completed ? 'DONE' : 'INCOMPLETE'} />
              ))}
            </StatusItems>
          </Col>
        </Row>
      </RepositCard>
      <RepositCard title="Account Managers">
        <Container>
          <Row>
            <Col lg={12} style={{ padding: 0 }}>
              <Table>
                <Tbody>
                  <Tr>
                    <Th width={250}>Sales</Th>
                    <Td style={{ paddingLeft: 10 }}>{salesDisplayName}</Td>
                    <Td>
                      {canManageAccountManagers ? (
                        <RowActions>
                          <Button onClick={() => setIsAssignOpen(true)}>{salesOwner ? 'Change' : 'Assign'}</Button>
                          {salesOwner ? (
                            <Button buttonType="warning" onClick={() => setIsUnassignOpen(true)}>
                              Unassign
                            </Button>
                          ) : null}
                        </RowActions>
                      ) : null}
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
            </Col>
          </Row>
        </Container>
      </RepositCard>
      <IntegrationInfo integrationToken={integrationToken} />
      {canManageAccountManagers && isAssignOpen ? (
        <AssignAccountManagerModal
          organizationId={organizationId}
          currentInternalTeamMemberId={salesOwner ? salesOwner.internalTeamMember.id : undefined}
          onDismiss={() => setIsAssignOpen(false)}
        />
      ) : null}
      {canManageAccountManagers && isUnassignOpen && salesOwner ? (
        <UnassignAccountManagerConfirmModal
          organizationId={organizationId}
          displayName={salesOwner.internalTeamMember.displayName}
          onDismiss={() => setIsUnassignOpen(false)}
        />
      ) : null}
    </Container>
  );
};
